import React, { useState, useEffect } from 'react';
import { ComponentConfig } from '@measured/puck';
import CustomTooltip from '../../../components/common/tooltip';
import { MarginProps, marginFields } from '../../puck/reusable-props/margin';
import { WidthProps, widthField } from '../../puck/reusable-props/width';
import { PaddingProps, paddingFields } from '../../puck/reusable-props/padding';
import ArrowDown from '../../../assets/svg/ArrowDown';
import {
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import { ReduxSelectorProps } from '../../services/constants';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import { resolveShowComponentField, getBorderProps } from '../../utils/helper';
import Border, { IBorderValueProps } from '../../puck/reusable-props/Border';
import { backgroungFields } from '../../puck/reusable-props/backgroungFields';
import '../text-editor/style.scss';
import WithAccordion from '../customComponents/CustomAccordion';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import { Switch, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import TextArrayField from './TextArrayField';

export type CarouselPanelProps = {
  panels: string[];
  autoSlide: boolean;
  slideInterval: number;
  border: IBorderValueProps;
  background: {
    color: string;
    isInherit: boolean;
  };
  customiseArrows: {
    isShowDots: boolean;
    isShowArrows: boolean;
    arrowColor: string;
  };
} & MarginProps &
  showComponentProps &
  WidthProps &
  PaddingProps;

export const CarouselPanel = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<CarouselPanelProps> => {
  return {
    //need to change tooltip
    label: (
      <CustomTooltip title="Carousel Panel">
        <span>Carousel Panel</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      panels: {
        type: 'custom',
        label: 'Footer',
        render: ({ value, onChange }) => {
          return <TextArrayField value={value} onChange={onChange} />;
        },
      },
      autoSlide: {
        type: 'radio',
        label: 'Auto Slide',
        options: [
          { value: true, label: 'Yes' },
          { value: false, label: 'No' },
        ],
      },
      slideInterval: {
        type: 'number',
        label: 'Slide Interval (ms)',
        min: 1,
        max: 10000,
      },
      customiseArrows: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <WithAccordion title="Custom Active Tab">
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">Show Arrow</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title={'Toggling this will show arrows.'}
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-1 w-1" />
                </Tooltip>
                <Switch
                  checked={value?.isShowArrows}
                  onChange={event =>
                    onChange({
                      ...value,
                      isShowArrows: event.target.checked,
                    })
                  }
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>
            <div className="flex justify-between items-center mb-3">
              <p className="text-sm font-semibold">Show Dots</p>
              <div className="flex gap-0.5 items-center">
                <Tooltip
                  title={'Toggling this will show dots.'}
                  placement="top-start">
                  <InfoIcon fontSize="small" className="h-1 w-1" />
                </Tooltip>
                <Switch
                  checked={value?.isShowDots}
                  onChange={event =>
                    onChange({
                      ...value,
                      isShowDots: event.target.checked,
                    })
                  }
                  inputProps={{ 'aria-label': 'controlled' }}
                />
              </div>
            </div>
            <ColorPicker
              label="Arrow Color"
              tooltip="backgroundColor"
              onChange={color =>
                onChange({
                  ...value,
                  arrowColor: color,
                })
              }
              value={value?.arrowColor}
            />
          </WithAccordion>
        ),
      },
      ...marginFields,
      ...paddingFields,
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      ...backgroungFields,
      ...widthField,
    },
    defaultProps: {
      showComponent: ['desktop', 'tablet', 'mobile'],
      panels: [
        '<p class="ql-align-center" style="line-height: 1.5;"><span style="color: rgb(230, 0, 0); font-size: 16px;">LIVE:</span><span style="color: rgb(255, 255, 255); font-size: 16px;"> 77 People are watching this product</span></p>',
        '<p class="ql-align-center" style="line-height: 1.5;"><span style="font-size: 10px;">⭐⭐⭐⭐⭐      </span><span style="font-size: 16px; color: rgb(255, 255, 255);">Over 50,000 Happy Customer</span></p>',
      ],
      autoSlide: true,
      slideInterval: 2000,
      border: {
        deviceType: 'desktop',
        borderBottomLeft: {
          desktop: '15',
          tablet: '15',
          mobile: '15',
        },
        borderBottomRight: {
          desktop: '15',
          tablet: '15',
          mobile: '15',
        },
        borderTopLeft: {
          desktop: '15',
          tablet: '15',
          mobile: '15',
        },
        borderTopRight: {
          desktop: '15',
          tablet: '15',
          mobile: '15',
        },
        borderTopWidth: {
          desktop: 0,
          tablet: 0,
          mobile: 0,
        },
        borderRightWidth: {
          desktop: 0,
          tablet: 0,
          mobile: 0,
        },
        borderBottomWidth: {
          desktop: 0,
          tablet: 0,
          mobile: 0,
        },
        borderLeftWidth: {
          desktop: 0,
          tablet: 0,
          mobile: 0,
        },
        borderWidth: {
          desktop: 0,
          tablet: 0,
          mobile: 0,
        },
        borderStyle: {
          desktop: 'border-solid',
          tablet: 'border-solid',
          mobile: 'border-solid',
        },
        borderColor: {
          desktop: '#000000',
          tablet: '#000000',
          mobile: '#000000',
        },
      },
      background: {
        color: '#000000',
        isInherit: false,
      },
      margin: {
        desktop: {
          top: '16',
          bottom: '16',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '16',
          bottom: '16',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '12',
          bottom: '12',
          right: '0',
          left: '0',
        },
      },
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      width: 'w-full',
      customiseArrows: {
        isShowDots: true,
        isShowArrows: true,
        arrowColor: '#ffffff',
      },
    },
    resolveFields: ({ props }, { fields }) =>
      resolveShowComponentField(props.id, fields),
    render: ({
      panels,
      autoSlide,
      slideInterval,
      margin,
      padding,
      width,
      border,
      background,
      showComponent,
      editMode,
      customiseArrows,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const paddingStyles = getPaddingStyles(padding, currentDeviceType);
      const [currentIndex, setCurrentIndex] = useState(0);
      console.log(currentIndex, 'currentIndex');

      // Handle auto-slide
      useEffect(() => {
        if (autoSlide && panels.length > 1) {
          const interval = setInterval(() => {
            setCurrentIndex(prevIndex => (prevIndex + 1) % panels.length);
          }, slideInterval);
          return () => clearInterval(interval);
        }
      }, [autoSlide, slideInterval, panels.length]);

      // Navigate to previous panel
      const goToPrevious = () => {
        setCurrentIndex(prevIndex =>
          prevIndex === 0 ? panels.length - 1 : prevIndex - 1,
        );
      };

      // Navigate to next panel
      const goToNext = () => {
        setCurrentIndex(prevIndex => (prevIndex + 1) % panels.length);
      };

      // Navigate to specific panel
      const goToPanel = (index: React.SetStateAction<number>) => {
        setCurrentIndex(index);
      };

      if (!panels || panels.length === 0) {
        return (
          <div className="w-full text-center p-4">No panels to display</div>
        );
      }
      const panelRef = React.useRef<HTMLDivElement>(null);

      const PanelWidth = panelRef?.current?.offsetWidth || 0;

      return (
        <>
          {showComponent?.includes(currentDeviceType) && (
            <div
              className={`relative ${width} overflow-hidden text-editor-content min-h-12 `}
              style={{
                ...marginStyles,
                backgroundColor: background?.isInherit
                  ? 'inherit'
                  : background?.color,
                ...paddingStyles,
                ...getBorderProps(border, currentDeviceType),
                transition: 'background-color 0.3s',
              }}>
              <div
                className="flex transition-transform duration-500 ease-in-out text-editor-content"
                style={{
                  transform: `translateX(-${currentIndex * PanelWidth}px)`,
                }}>
                {panels.map((panel, index) => (
                  <div
                    ref={panelRef}
                    key={index}
                    className="w-full flex-shrink-0 px-12"
                    style={{ minWidth: '100%' }}>
                    <div
                      className="w-full h-full"
                      dangerouslySetInnerHTML={{ __html: panel }}
                    />
                  </div>
                ))}
              </div>

              {panels.length > 1 && (
                <>
                  {customiseArrows?.isShowArrows && (
                    <>
                      <button
                        type="button"
                        onClick={goToPrevious}
                        className="absolute left-1 top-1/2 transform -translate-y-1/2 text-white"
                        aria-label="Previous panel">
                        <span
                          style={{
                            display: 'inline-block',
                            transform: 'rotate(90deg)',
                          }}>
                          <ArrowDown fill={customiseArrows?.arrowColor} />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={goToNext}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white"
                        aria-label="Next panel">
                        <span
                          style={{
                            display: 'inline-block',
                            transform: 'rotate(270deg)',
                          }}>
                          <ArrowDown fill={customiseArrows?.arrowColor} />
                        </span>
                      </button>
                    </>
                  )}
                  {customiseArrows?.isShowDots && (
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex space-x-2">
                      {panels.map((_, index) => (
                        <button
                          type="button"
                          key={index}
                          onClick={() => goToPanel(index)}
                          className={`w-3 h-3 rounded-full ${
                            currentIndex === index
                              ? 'bg-gray-800'
                              : 'bg-gray-400'
                          } focus:outline-none`}
                          aria-label={`Go to panel ${index + 1}`}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      );
    },
  };
};
